/**
 * Auth core — provider registry and AuthService wrapper.
 * Provider implementations live in auth-auth0.ts and auth-cognito.ts.
 */
import type { AuthClient, AuthService, AuthProvider, AuthProviderContext, SignInResult, User } from './types'

const MAX_LISTENERS = 100

/**
 * How long the password given to `signUp` stays in memory so `confirmSignUp` can
 * complete the sign-in itself. Long enough to cover reading a code out of an email,
 * short enough that an abandoned signup does not leave a password sitting in a tab
 * for the rest of the session.
 */
const PENDING_SIGN_UP_TTL_MS = 15 * 60 * 1000

// --- Provider registry ---

const providers = new Map<string, AuthProvider>()

export function registerAuthProvider(name: string, factory: AuthProvider) {
  providers.set(name, factory)
}

// Built-in "none" provider
registerAuthProvider('none', async () => ({
  login: async () => {},
  logout: async () => {},
  getUser: async () => undefined,
  getTokenSilently: async () => 'none',
  isAuthenticated: async () => true
}))

export interface AuthProviderConfig {
  provider: string
  [key: string]: unknown
}

/**
 * Validate the auth config block handed to a provider factory before it
 * touches the underlying auth library. The block comes from the backend's
 * public init response, not from local options — so a mismatch means the
 * backend isn't configured for this provider, and configuring the library
 * with empty values would only fail later with an unrelated-looking error.
 */
export function assertProviderConfig(
  config: Record<string, unknown>,
  provider: string,
  requiredKeys: readonly string[]
): Record<string, unknown> {
  const reported = typeof config.provider === 'string' && config.provider ? config.provider : undefined
  if (reported !== provider) {
    throw new Error(
      `[foundation-sdk] createFoundation was given the "${provider}" auth provider, but the backend ` +
      `config endpoint reports provider "${reported ?? 'none'}". Auth factories are configured from ` +
      `the init response, not local options. Set authenticationProvider: "${provider}" in the backend ` +
      `config.json and redeploy (app:deploy) so /api/v1/public/init returns the ${provider} auth config.`
    )
  }
  const block = config[provider]
  const record = block && typeof block === 'object' ? block as Record<string, unknown> : undefined
  const missing = requiredKeys.filter(key => {
    const value = record?.[key]
    return typeof value !== 'string' || value.trim() === ''
  })
  if (!record || missing.length > 0) {
    throw new Error(
      `[foundation-sdk] The backend reports provider "${provider}" but its config block is incomplete ` +
      `(missing or empty: ${missing.join(', ')}). Check the ${provider} settings in the backend ` +
      `config.json and redeploy (app:deploy) so /api/v1/public/init returns the full ${provider} config.`
    )
  }
  return record
}

export async function createAuthClient(config: AuthProviderConfig, ctx: AuthProviderContext): Promise<AuthClient> {
  const factory = providers.get(config.provider)
  if (!factory) {
    throw new Error(
      `Auth provider "${config.provider}" not registered. ` +
      `Import "foundation-sdk/${config.provider}" to register it.`
    )
  }
  return factory(config, ctx)
}

// --- AuthService wrapper ---

export interface AuthServiceOptions {
  /** Sign in automatically after a successful `confirmSignUp`. Default true. */
  autoSignInAfterConfirm?: boolean
}

export function createAuthService(
  client: AuthClient,
  options: AuthServiceOptions = {}
): AuthService & { _initUser(): Promise<void> } {
  const autoSignInAfterConfirm = options.autoSignInAfterConfirm !== false
  let user: User | null = null
  const listeners: Array<(user: User | null) => void> = []

  /**
   * A confirmation code proves the user owns the address, but confirming does not
   * create a session — so without this the caller has to bounce them to a login form
   * to retype the password they entered a minute earlier. The credentials live in this
   * closure only: never localStorage, never sessionStorage, never a cookie. They are
   * dropped the moment they are used, superseded by another auth call, or stale.
   */
  let pendingSignUp: { email: string; password: string; expiresAt: number } | null = null
  let pendingSignUpTimer: ReturnType<typeof setTimeout> | null = null

  function clearPendingSignUp(): void {
    pendingSignUp = null
    if (pendingSignUpTimer !== null) {
      clearTimeout(pendingSignUpTimer)
      pendingSignUpTimer = null
    }
  }

  function rememberPendingSignUp(email: string, password: string): void {
    clearPendingSignUp()
    if (!autoSignInAfterConfirm || !email || !password) return
    pendingSignUp = { email, password, expiresAt: Date.now() + PENDING_SIGN_UP_TTL_MS }
    pendingSignUpTimer = setTimeout(clearPendingSignUp, PENDING_SIGN_UP_TTL_MS)
    // Background tabs throttle timers and Node would hold the process open for one,
    // so `expiresAt` is the guard that actually decides; the timer only scrubs early.
    const handle = pendingSignUpTimer as unknown as { unref?: () => void }
    if (typeof handle.unref === 'function') handle.unref()
  }

  /** Returns the held credentials for `email` exactly once, then forgets them. */
  function takePendingSignUp(email: string): { email: string; password: string } | null {
    const pending = pendingSignUp
    clearPendingSignUp()
    if (!pending || pending.expiresAt <= Date.now()) return null
    // Compare loosely (Cognito treats email aliases case-insensitively) but sign in with
    // the username signUp actually used, which is the one the pool has a record for.
    if (pending.email.trim().toLowerCase() !== email.trim().toLowerCase()) return null
    return { email: pending.email, password: pending.password }
  }

  function notifyListeners() {
    listeners.forEach(fn => { try { fn(user) } catch { /* */ } })
  }

  async function refreshUser() {
    const authUser = await client.getUser()
    user = authUser ? { id: authUser.id, email: authUser.email, name: authUser.name, picture: authUser.picture } : null
  }

  return {
    get user() { return user },
    get isAuthenticated() { return !!user },

    async getToken() {
      return client.getTokenSilently()
    },

    async login(options) {
      await client.login(options)
      await refreshUser()
      notifyListeners()
    },

    async logout(options) {
      clearPendingSignUp()
      await client.logout(options)
      user = null
      notifyListeners()
    },

    async handleCallback(url?) {
      if (!client.handleCallback) throw new Error('handleCallback not supported by this auth provider')
      await client.handleCallback(url)
      await refreshUser()
      notifyListeners()
    },

    async signIn(email, password) {
      if (!client.signIn) throw new Error('signIn not supported by this auth provider')
      // An explicit sign-in supersedes whatever signUp left behind, success or not.
      clearPendingSignUp()
      const result = await client.signIn(email, password)
      // Only refresh user state if the sign-in actually completed.
      // If a provider returns isSignedIn: false with a next step (e.g. CONFIRM_SIGN_UP),
      // the user isn't really signed in and we shouldn't populate the user ref.
      if (result.isSignedIn) {
        await refreshUser()
        notifyListeners()
      }
      return result
    },

    async signUp(email, password, metadata) {
      if (!client.signUp) throw new Error('signUp not supported by this auth provider')
      const result = await client.signUp(email, password, metadata)
      rememberPendingSignUp(email, password)
      return result
    },

    async confirmSignUp(email, code) {
      if (!client.confirmSignUp) throw new Error('confirmSignUp not supported by this auth provider')
      await client.confirmSignUp(email, code)

      const pending = takePendingSignUp(email)
      if (!pending || !client.signIn) return { isSignedIn: false }

      let result: SignInResult
      try {
        result = await client.signIn(pending.email, pending.password)
      } catch {
        // The account is confirmed — that is what this call promised, and turning a
        // failed convenience sign-in into a thrown confirmation would make the caller
        // replay a code that is already spent. Report no session and let them show
        // the sign-in form.
        return { isSignedIn: false }
      }
      if (result.isSignedIn) {
        await refreshUser()
        notifyListeners()
      }
      return result
    },

    async resendSignUpCode(email) {
      if (!client.resendSignUpCode) throw new Error('resendSignUpCode not supported by this auth provider')
      await client.resendSignUpCode(email)
    },

    async forgotPassword(email) {
      if (!client.forgotPassword) throw new Error('forgotPassword not supported by this auth provider')
      await client.forgotPassword(email)
    },

    async resetPassword(email, code, newPassword) {
      if (!client.resetPassword) throw new Error('resetPassword not supported by this auth provider')
      // Whatever signUp handed us is stale the moment the password changes.
      clearPendingSignUp()
      await client.resetPassword(email, code, newPassword)
    },

    onChange(callback) {
      if (listeners.length >= MAX_LISTENERS) {
        console.warn('[Foundation SDK] Auth listener limit reached.')
        return () => {}
      }
      listeners.push(callback)
      return () => {
        const idx = listeners.indexOf(callback)
        if (idx > -1) listeners.splice(idx, 1)
      }
    },

    async _initUser() {
      const authenticated = await client.isAuthenticated()
      if (authenticated) await refreshUser()
    }
  }
}
